import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import SaveIcon from '@mui/icons-material/Save';

import * as adminApi from '../../api/admin';
import type {
  InsightsSettings as InsightsSettingsType,
  InsightsSettingsUpdate,
} from '../../api/admin';

/**
 * AI / Insights settings (superadmin only).
 *
 * One provider, OpenAI (OpenRouter was removed 2026-10): one key serves
 *   - Whisper, which transcribes the capture-app anchor audio per marker
 *     window (the transcript / caption track), and
 *   - the chat model, which writes the AI Coach narration, the coach's
 *     debrief points, and reads conference badges at intake.
 *
 * Stored in `public.platform_settings` (key='insights_config'). The key
 * is write-only — the response carries `has_openai_api_key`, never the
 * secret. Password semantics as on Mail settings:
 *   - field left blank in the form         → keep stored value
 *   - field typed with a new value         → store new value
 *   - "Clear stored key" switch ON         → forget stored value
 *
 * Both toggles default OFF and are the operational kill switches — the
 * worker reads them on every job, no redeploy needed. Prices are $ per 1M
 * tokens, copied from OpenAI's price list; left empty, spend is recorded
 * as tokens with an unknown cost.
 */

type FormState = {
  openai_api_key: string;
  clear_openai_api_key: boolean;
  marker_labels_enabled: boolean;
  coaching_enabled: boolean;
  llm_model: string;
  // Kept as text so a half-typed number doesn't fight the input.
  price_in: string;
  price_out: string;
};

const priceText = (v: number | null) => (v === null ? '' : String(v));

function settingsToForm(s: InsightsSettingsType): FormState {
  return {
    openai_api_key: '',
    clear_openai_api_key: false,
    marker_labels_enabled: s.marker_labels_enabled,
    coaching_enabled: s.coaching_enabled,
    llm_model: s.llm_model,
    price_in: priceText(s.llm_price_input_per_mtok),
    price_out: priceText(s.llm_price_output_per_mtok),
  };
}

/** '' → null (unset); a non-negative number → that number; else undefined
 *  (invalid — blocks saving). */
function parsePrice(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

export function AiInsightsSettings({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const [server, setServer] = useState<InsightsSettingsType | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Chat models the saved OpenAI key can use (for the model dropdown).
  // null until loaded; [] + modelsError when the call failed.
  const [models, setModels] = useState<string[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .getInsightsSettings()
      .then((s) => {
        setServer(s);
        setForm(settingsToForm(s));
      })
      .catch((err: unknown) =>
        setLoadError(
          (err as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? 'failed to load AI/insights settings',
        ),
      );
  }, []);

  // The list needs the key, so it loads once a key has been SAVED.
  useEffect(() => {
    if (server?.has_openai_api_key !== true) {
      setModels(null);
      return;
    }
    adminApi
      .getOpenAIModels()
      .then((r) => {
        setModels(r.models);
        setModelsError(null);
      })
      .catch((err: unknown) => {
        setModelsError(
          (err as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? 'failed to load the OpenAI model list',
        );
        setModels([]);
      });
  }, [server?.has_openai_api_key]);

  // Both features need the OpenAI key; the AI Coach also needs a model.
  const haveKey =
    server !== null &&
    !form?.clear_openai_api_key &&
    (server.has_openai_api_key || (form?.openai_api_key.length ?? 0) > 0);
  const modelSet = form !== null && form.llm_model.trim() !== '';
  const canEnableTranscription = haveKey;
  const canEnableCoaching = haveKey && modelSet;

  const priceIn = form ? parsePrice(form.price_in) : null;
  const priceOut = form ? parsePrice(form.price_out) : null;
  const pricesValid = priceIn !== undefined && priceOut !== undefined;

  const dirty =
    server !== null &&
    form !== null &&
    (form.openai_api_key !== '' ||
      form.clear_openai_api_key ||
      form.marker_labels_enabled !== server.marker_labels_enabled ||
      form.coaching_enabled !== server.coaching_enabled ||
      form.llm_model !== server.llm_model ||
      priceIn !== server.llm_price_input_per_mtok ||
      priceOut !== server.llm_price_output_per_mtok);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (server === null || form === null || !pricesValid) return;
    setSaveError(null);
    setSavedAt(null);
    setSaving(true);
    try {
      const patch: InsightsSettingsUpdate = {};
      if (form.openai_api_key !== '') patch.openai_api_key = form.openai_api_key;
      if (form.clear_openai_api_key) patch.clear_openai_api_key = true;
      if (form.marker_labels_enabled !== server.marker_labels_enabled)
        patch.marker_labels_enabled = form.marker_labels_enabled;
      if (form.coaching_enabled !== server.coaching_enabled)
        patch.coaching_enabled = form.coaching_enabled;
      if (form.llm_model !== server.llm_model) patch.llm_model = form.llm_model.trim();
      if (priceIn !== server.llm_price_input_per_mtok)
        patch.llm_price_input_per_mtok = priceIn ?? null;
      if (priceOut !== server.llm_price_output_per_mtok)
        patch.llm_price_output_per_mtok = priceOut ?? null;

      const next = await adminApi.updateInsightsSettings(patch);
      setServer(next);
      setForm(settingsToForm(next));
      setSavedAt(Date.now());
    } catch (err: unknown) {
      setSaveError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to save settings',
      );
    } finally {
      setSaving(false);
    }
  };

  const modelHelp = modelsError ? (
    <>
      Couldn&rsquo;t load the OpenAI model list ({modelsError}) — type a model
      id. See{' '}
      <MuiLink
        href="https://platform.openai.com/docs/models"
        target="_blank"
        rel="noopener noreferrer"
      >
        platform.openai.com/docs/models
      </MuiLink>
      .
    </>
  ) : models === null ? (
    'Save the OpenAI key first — this becomes a list of the models your key can use.'
  ) : (
    <>
      {models.length} chat model{models.length === 1 ? '' : 's'} available to
      your key. A small model is right for this work; a dated id (e.g.{' '}
      <code>…-2026-03-17</code>) keeps the output stable until you change it.
    </>
  );

  return (
    <Stack spacing={3} sx={{ maxWidth: 760 }}>
      {!embedded && <Typography variant="h4">AI / Insights settings</Typography>}

      <Alert severity="info">
        One OpenAI key drives the AI features. <strong>Whisper</strong>{' '}
        transcribes the capture-app anchor audio for the transcript and
        captions on the Cognitive trace. The <strong>chat model</strong>{' '}
        writes the AI Coach read of each session and the coach&rsquo;s debrief
        points, and reads conference badges at intake.
      </Alert>

      {/* An enterprise compliance control, not a generic warning: the items
          are REQUIREMENTS to confirm before enabling. */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'warning.main',
          borderRadius: 1,
          p: 2,
          bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
        }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 800, letterSpacing: '0.08em', color: 'warning.dark' }}
        >
          Compliance impact
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          The anchor audio captures whatever&rsquo;s audible at the kiosk during
          a session — likely <strong>PHI-shaped</strong> in clinical settings.
          Transcription sends that audio to <strong>OpenAI</strong>. Confirm
          before enabling:
        </Typography>
        <Stack component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }} spacing={0.25}>
          <Typography component="li" variant="body2">
            A signed <strong>BAA</strong> with OpenAI
          </Typography>
          <Typography component="li" variant="body2">
            The platform&rsquo;s <strong>HIPAA posture</strong> reviewed for this
            data flow (ROADMAP #9)
          </Typography>
          <Typography component="li" variant="body2">
            OpenAI <strong>zero-retention</strong> terms where required
          </Typography>
        </Stack>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            OpenAI API key
          </Typography>

          {loadError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {loadError}
            </Alert>
          )}
          {!server && !loadError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {server && form && (
            <Box component="form" onSubmit={handleSave} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  OpenAI
                </Typography>
                <Chip
                  size="small"
                  label={server.has_openai_api_key ? 'Configured' : 'Not set'}
                  color={server.has_openai_api_key ? 'success' : 'default'}
                  variant={server.has_openai_api_key ? 'filled' : 'outlined'}
                />
              </Stack>
              <TextField
                label="OpenAI API key"
                fullWidth
                type="password"
                value={form.openai_api_key}
                onChange={(e) =>
                  setForm((f) => (f ? { ...f, openai_api_key: e.target.value } : f))
                }
                placeholder={server.has_openai_api_key ? '••••••' : ''}
                helperText={
                  form.clear_openai_api_key
                    ? 'Key will be cleared on save.'
                    : 'Used for Whisper and the chat model. Get one at platform.openai.com.'
                }
                disabled={saving || form.clear_openai_api_key}
              />
              {server.has_openai_api_key && (
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Switch
                      size="small"
                      checked={form.clear_openai_api_key}
                      onChange={(e) =>
                        setForm((f) =>
                          f
                            ? {
                                ...f,
                                clear_openai_api_key: e.target.checked,
                                openai_api_key: e.target.checked ? '' : f.openai_api_key,
                              }
                            : f,
                        )
                      }
                      disabled={saving}
                    />
                  }
                  label={<Typography variant="caption">Clear stored OpenAI key</Typography>}
                />
              )}

              <Divider sx={{ my: 3 }} />
              <Typography variant="overline" color="text.secondary">
                Transcription
              </Typography>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.marker_labels_enabled}
                      onChange={(e) =>
                        setForm((f) =>
                          f ? { ...f, marker_labels_enabled: e.target.checked } : f,
                        )
                      }
                      disabled={saving || !canEnableTranscription}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Transcribe anchor audio</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {canEnableTranscription
                          ? 'Whisper transcribes each session once its anchor audio and cognitive scores are in.'
                          : 'An OpenAI key is required before this can be turned on.'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Divider sx={{ my: 3 }} />
              <Typography variant="overline" color="text.secondary">
                Chat model
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                Writes the AI Coach read from each session&rsquo;s{' '}
                <strong>derived metric comparison</strong> (numbers only — no
                audio, transcripts or names), the coach&rsquo;s debrief points,
                and reads conference badges at intake.
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  {models !== null && models.length > 0 ? (
                    <Autocomplete
                      options={models}
                      freeSolo
                      value={form.llm_model}
                      onChange={(_e, v) =>
                        setForm((f) => (f ? { ...f, llm_model: v ?? '' } : f))
                      }
                      onInputChange={(_e, v, reason) => {
                        if (reason === 'input')
                          setForm((f) => (f ? { ...f, llm_model: v } : f));
                      }}
                      disabled={saving}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Chat model"
                          fullWidth
                          helperText={modelHelp}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      label="Chat model"
                      fullWidth
                      value={form.llm_model}
                      onChange={(e) =>
                        setForm((f) => (f ? { ...f, llm_model: e.target.value } : f))
                      }
                      helperText={modelHelp}
                      disabled={saving}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Input price ($ per 1M tokens)"
                    fullWidth
                    inputMode="decimal"
                    value={form.price_in}
                    onChange={(e) =>
                      setForm((f) => (f ? { ...f, price_in: e.target.value } : f))
                    }
                    error={priceIn === undefined}
                    helperText={priceIn === undefined ? 'Enter a number, or leave empty.' : ' '}
                    disabled={saving}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Output price ($ per 1M tokens)"
                    fullWidth
                    inputMode="decimal"
                    value={form.price_out}
                    onChange={(e) =>
                      setForm((f) => (f ? { ...f, price_out: e.target.value } : f))
                    }
                    error={priceOut === undefined}
                    helperText={priceOut === undefined ? 'Enter a number, or leave empty.' : ' '}
                    disabled={saving}
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    Copy both prices for the chosen model from{' '}
                    <MuiLink
                      href="https://openai.com/api/pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      openai.com/api/pricing
                    </MuiLink>
                    . OpenAI reports tokens, not money — without prices the AI
                    spend card shows the calls with an unknown cost.
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.coaching_enabled}
                        onChange={(e) =>
                          setForm((f) => (f ? { ...f, coaching_enabled: e.target.checked } : f))
                        }
                        disabled={saving || !canEnableCoaching}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Enable AI Coach narration</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {canEnableCoaching
                            ? 'Each session is narrated after its cognitive/motion scoring; the card falls back to its derived copy when this is off or a call fails.'
                            : 'An OpenAI key and a chat model are required before this can be turned on.'}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>

              {saveError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {saveError}
                </Alert>
              )}
              {savedAt !== null && !saveError && !dirty && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Saved. The next insights-worker job pull will pick up the new
                  settings — no redeploy needed.
                </Alert>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                  }
                  disabled={!dirty || saving || !pricesValid}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setForm(settingsToForm(server))}
                  disabled={!dirty || saving}
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Settings are stored in <code>public.platform_settings</code> (key=
            <code>insights_config</code>). The API key is write-only — the
            server returns an &ldquo;is set&rdquo; flag only; the plaintext is
            never echoed back. The audit trail records every change with the
            secret redacted.
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
}
